import { pathDot } from './google-map.style';

// The duration of drawing the bezier curve
const DURATION = 1000;
// The number of points of the curve to draw on the map
const NUM_PTS = 1000;

// Takes in three points of format [lat, long]
// Returns a function that takes in a number from 0 to 1
// and returns a point along a curve from the first point to the third point
// where the tangents of the curve at 0 and 1 intersect at the second point
// See: https://en.wikipedia.org/wiki/B%C3%A9zier_curve#Quadratic_Bézier_curves
const bezierFn = ([x1, y1], [x2, y2], [x3, y3]) => {
  return t => [
    (1 - t) ** 2 * x1 + 2 * (1 - t) * t * x2 + t ** 2 * x3,
    (1 - t) ** 2 * y1 + 2 * (1 - t) * t * y2 + t ** 2 * y3,
  ];
};

// Takes in two points of format [lat, long]
// Returns the great-circle distance between the two points in km
const haversineDistance = ([latOne, longOne], [latTwo, longTwo]) => {
  const R = 6371; // Approximate radius of the Earth
  const degreesToRadians = n => (n * Math.PI) / 180;
  const haversine = n => Math.sin(n / 2) ** 2;
  const reverseHaversine = n => 2 * Math.atan2(Math.sqrt(n), Math.sqrt(1 - n));

  // If
  //   d is the great-circle distance between two points
  //   R is the radius of the Earth
  //   a1, a2 are the latitude and longitude of the first point
  //   b1, b2 are the latitude and longitude of the second point
  // Then
  //   haversine(d/R) = haversine(b1 - a1) + cos(a1) * cos(b1) * haversine(b2 - a2)
  const a =
    haversine(degreesToRadians(latTwo - latOne)) +
    Math.cos(degreesToRadians(latOne)) *
      Math.cos(degreesToRadians(latTwo)) *
      haversine(degreesToRadians(longTwo - longOne));

  return R * reverseHaversine(a);
};

// Very rough approximate of degrees between two points
// given that one degree of latitude ranges from 110.567km to 111.699km
const degHaversine = (p1, p2) => haversineDistance(p1, p2) / 111.111;

// Takes in two points of format [lat, long]
// Presumably it returns a point which creates an isosceles triangle with the given points
// but who knows
const isosceles = ([x1, y1], [x2, y2]) => {
  const dX = x2 - x1;
  const dY = y2 - y1;
  const l = degHaversine([x1, y1], [x2, y2]);

  const alpha = dX === 0 ? 0 : Math.atan(dY / dX);
  const beta = Math.atan(0.0004);
  const theta = 2 * Math.PI - alpha - beta;

  const h = Math.sqrt((0.0002 * l) ** 2 + (0.5 * l) ** 2);

  return [x1 - 0.87 * h * Math.sin(theta), y1 - h * Math.cos(theta)];
};

// Takes a Google Map object and an array of points of format [lat, long]
// Draws a bezier line along the given points on the Map object over 1 second
export function mapBezier({ map, points }) {
  if (!map || !points) {
    return false;
  }

  const bezierPoints = [points[0], isosceles(...points), points[1]];

  const bezierPoint = bezierFn(...bezierPoints);
  const interval = DURATION / NUM_PTS;

  [...Array(NUM_PTS).keys()].forEach(index => {
    setTimeout(() => {
      const position = new google.maps.LatLng(
        ...bezierPoint(index / NUM_PTS) // scale the index to be between 0 and 1
      );
      const markerSettings = {
        map,
        position,
        icon: pathDot,
      };
      new google.maps.Marker(markerSettings);
    }, index * interval);
  });
}
